// ─────────────────────────────────────────────────────────────────────────────
// Shared auth-screen design tokens
//
// One source of truth for the logged-out / account-entry screens (login, register,
// set-password, portal). The aesthetic is a crisp WHITE canvas with small, subtle
// teal gradient washes — airy and light, accented (not dominated) by the brand
// teal-blue #427898. Replaces the styling helpers that used to be copy-pasted
// across the five auth pages.
// ─────────────────────────────────────────────────────────────────────────────

export const ACCENT = '#427898';

// Off-white canvas with one big, soft teal "bloom" glowing from the lower-centre
// — the SecureSafe look ("small gradients"): a diffuse light patch that fades to
// a calm light-grey at the edges. Applied to the full-page auth background and
// the light hero panel.
export const authBackground = `
    radial-gradient(1500px 1050px at 50% 80%, rgba(66,120,152,0.50) 0%, rgba(66,120,152,0.16) 36%, rgba(66,120,152,0) 64%),
    radial-gradient(900px 700px at 50% 118%, rgba(78,144,181,0.32) 0%, rgba(78,144,181,0) 60%),
    linear-gradient(180deg, #ffffff 0%, #f3f9fb 100%)
`;

// The shared primary CTA — flat solid teal (SecureSafe uses a flat button, not a
// gradient). Every auth button uses this so all five screens match.
export const primaryButtonSx = {
    backgroundColor: '#427898',
    borderRadius: '24px',
    py: 1.5,
    fontWeight: 600,
    fontSize: 15,
    textTransform: 'none',
    boxShadow: '0 2px 8px rgba(66,120,152,0.22)',
    transition: 'background-color 0.2s, box-shadow 0.2s',
    '&:hover': {
        backgroundColor: '#356382',
        boxShadow: '0 4px 14px rgba(66,120,152,0.32)',
    },
    '&.Mui-disabled': {
        color: 'rgba(255,255,255,0.85)',
        backgroundColor: '#8fb0c4',
    },
};

// Standard-variant TextField underline in brand teal (dedupes 4 copies).
export const underlineStyle = {
    '& .MuiInput-underline:before': { borderBottomColor: '#dce3ea' },
    '& .MuiInput-underline:hover:before': { borderBottomColor: '#427898' },
    '& .MuiInput-underline:after': { borderBottomColor: '#427898' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#427898' },
};

// Translucent frosted "login box" — a light card with slight opacity so the
// teal bloom tints softly through it (the SecureSafe look). Aligned with
// glassSurfaceStrong in src/styles/glass.js. Used for the login/register form
// column and the single-column auth cards alike.
export const authGlassCardSx = {
    p: { xs: 3, sm: 4 },
    borderRadius: '20px',
    backgroundColor: 'rgba(255,255,255,0.72)',
    backdropFilter: 'blur(20px) saturate(140%)',
    WebkitBackdropFilter: 'blur(20px) saturate(140%)',
    border: '1px solid rgba(255,255,255,0.85)',
    boxShadow: '0 1px 2px rgba(16,42,66,0.04), 0 24px 60px -22px rgba(30,58,82,0.24)',
};

// Centered single-column card = the glass box, given a width + entrance.
export const authCardSx = {
    width: '100%',
    maxWidth: 400,
    ...authGlassCardSx,
    animation: 'fadeSlideIn 0.35s ease both',
    '@keyframes fadeSlideIn': {
        from: { opacity: 0, transform: 'translateY(16px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
};

// Inline error banner.
export const errorBoxSx = {
    backgroundColor: '#fff0f0',
    border: '1px solid #ffcdd2',
    borderRadius: 2,
    px: 2,
    py: 1.25,
    mb: 2,
};

// Reusable keyframe object for pages that animate their own container.
export const fadeSlideIn = {
    '@keyframes fadeSlideIn': {
        from: { opacity: 0, transform: 'translateY(16px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
};
